import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Eye, Calendar, User, Tag, Link2, ExternalLink } from "lucide-react";
import Layout from "../../components/Layout";
import { PostsService, Post } from "../../services/postsService";
import { formatDateTime } from "../../utils/dateFormatter";
import defaultThumbnail from "../../assets/images/default-thumbnail.webp";
import "./../../styles/member/detailarticle.css";

const DetailArticle = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadArticle = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        const response = await PostsService.getPostById(parseInt(id));
        setArticle(response);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Có lỗi xảy ra khi tải bài viết"
        );
      } finally {
        setLoading(false);
      }
    };

    loadArticle();
  }, [id]);

  if (loading) {
    return (
      <Layout>
        <div className="detail-article-page">
          <div className="loading-state">Đang tải...</div>
        </div>
      </Layout>
    );
  }

  if (error || !article) {
    return (
      <Layout>
        <div className="detail-article-page">
          <div className="error-state">
            <p>{error || "Không tìm thấy bài viết"}</p>
            <button onClick={() => navigate("/posts/my-posts")} className="btn-primary">
              Quay lại danh sách
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="detail-article-page">
        {/* Header với background màu xanh */}
        <div className="detail-page-header">
          <div className="header-content-detail">
            <button onClick={() => navigate("/posts/my-posts")} className="btn-back-header">
              <ArrowLeft size={18} /> Quay lại
            </button>
            <h1 className="page-title">Chi tiết tin tức</h1>
            <div className="header-actions">
              <Link to={`/member/edit-article/${article.id}`} className="btn-edit-header">
                Chỉnh sửa
              </Link>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="detail-main-content">
          {/* Article header với thumbnail và tiêu đề */}
          <div className="article-header-section">
            <div className="article-thumbnail-wrapper">
              <img
                src={article.featuredImage || defaultThumbnail}
                alt={article.title}
                className="article-main-thumbnail"
                onError={(e) => {
                  const img = e.currentTarget as HTMLImageElement;
                  if (img.src !== defaultThumbnail) {
                    img.src = defaultThumbnail;
                  }
                }}
              />
            </div>
            <div className="article-header-info">
              <h2 className="article-main-title">{article.title}</h2>
              <div className="article-meta-info">
                <span className="meta-item">
                  <Tag size={14} />
                  {article.category?.name || "Chưa phân loại"}
                </span>
                <span className="meta-item">
                  <User size={14} />
                  {article.author?.fullName || article.author?.email || "N/A"}
                </span>
                <span className="meta-item slug-display">{article.slug}</span>
              </div>
            </div>
          </div>

          {/* Two column layout */}
          <div className="detail-two-column">
            {/* Left sidebar - Thông tin cơ bản */}
            <div className="detail-sidebar">
              <div className="sidebar-card">
                <h3 className="sidebar-title">Thông tin cơ bản</h3>
                
                <div className="sidebar-info-group">
                  <div className="sidebar-info-item">
                    <span className="sidebar-label">TIÊU ĐỀ</span>
                    <p className="sidebar-value">{article.title}</p>
                  </div>

                  <div className="sidebar-info-item">
                    <span className="sidebar-label">LOẠI</span>
                    <p className="sidebar-value">{article.category?.name || "Chưa phân loại"}</p>
                  </div>

                  <div className="sidebar-info-item">
                    <span className="sidebar-label">TÁC GIẢ</span>
                    <p className="sidebar-value">
                      <User size={14} />
                      {article.author?.fullName || article.author?.email || "N/A"}
                    </p>
                  </div>

                  <div className="sidebar-info-item">
                    <span className="sidebar-label">NGÀY XUẤT BẢN</span>
                    <p className="sidebar-value">{formatDateTime(article.createdAt)}</p>
                  </div>

                  <div className="sidebar-info-item">
                    <span className="sidebar-label">SLUG</span>
                    <p className="sidebar-value slug-text-sidebar">{article.slug}</p>
                  </div>

                  <div className="sidebar-info-item">
                    <span className="sidebar-label">TRẠNG THÁI</span>
                    <p className="sidebar-value">
                      <span
                        className={`status-badge-sidebar ${
                          article.status === "approved"
                            ? "approved"
                            : article.status === "pending"
                            ? "pending"
                            : "rejected"
                        }`}
                      >
                        {article.status === "approved"
                          ? "Đã xuất bản"
                          : article.status === "pending"
                          ? "Chờ duyệt"
                          : "Từ chối"}
                      </span>
                    </p>
                  </div>

                  <div className="sidebar-info-item">
                    <span className="sidebar-label">NGÀY TẠO</span>
                    <p className="sidebar-value">{formatDateTime(article.createdAt)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right main content */}
            <div className="detail-main-area">
              {/* Tóm tắt */}
              {article.metaDescription && (
                <div className="content-section">
                  <h3 className="section-title">Tóm tắt</h3>
                  <div className="summary-box">
                    <p>{article.metaDescription}</p>
                  </div>
                </div>
              )}

              {/* Nội dung */}
              <div className="content-section">
                <h3 className="section-title">Nội dung</h3>
                <div
                  className="article-main-content"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </div>

              {/* SEO Keywords */}
              {(article.primaryKeyword || (article.secondaryKeywords && article.secondaryKeywords.length > 0)) && (
                <div className="content-section">
                  <h3 className="section-title">Từ khóa SEO</h3>
                  <div className="keywords-section">
                    {article.primaryKeyword && (
                      <div className="keyword-group">
                        <span className="keyword-label">Từ khóa chính:</span>
                        <span className="keyword-badge-main primary">{article.primaryKeyword}</span>
                      </div>
                    )}
                    {article.secondaryKeywords && article.secondaryKeywords.length > 0 && (
                      <div className="keyword-group">
                        <span className="keyword-label">Từ khóa phụ:</span>
                        <div className="keywords-wrap">
                          {article.secondaryKeywords.map((keyword, index) => (
                            <span key={index} className="keyword-badge-main secondary">
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Links */}
              {((article.internalLinks && article.internalLinks.length > 0) ||
                (article.externalLinks && article.externalLinks.length > 0)) && (
                <div className="content-section">
                  <h3 className="section-title">Liên kết</h3>
                  <div className="links-section">
                    {article.internalLinks && article.internalLinks.length > 0 && (
                      <div className="links-group">
                        <h4 className="links-group-title">Liên kết nội bộ:</h4>
                        <ul className="links-list-main">
                          {article.internalLinks.map((link, index) => (
                            <li key={index} className="link-item-main">
                              <Link2 size={14} />
                              <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="link-text"
                              >
                                {link}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {article.externalLinks && article.externalLinks.length > 0 && (
                      <div className="links-group">
                        <h4 className="links-group-title">Liên kết bên ngoài:</h4>
                        <ul className="links-list-main">
                          {article.externalLinks.map((link, index) => (
                            <li key={index} className="link-item-main">
                              <ExternalLink size={14} />
                              <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="link-text"
                              >
                                {link}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tags */}
              {article.tags && article.tags.length > 0 && (
                <div className="content-section">
                  <h3 className="section-title">Tags</h3>
                  <div className="tags-list-main">
                    {article.tags.map((tag, index) => (
                      <span key={index} className="tag-item-main">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Rejection Reason */}
              {article.status === "rejected" && article.rejectionReason && (
                <div className="content-section rejection-section">
                  <h3 className="section-title rejection-title">Lý do từ chối</h3>
                  <div className="rejection-box">
                    <p>{article.rejectionReason}</p>
                  </div>
                </div>
              )}

              {/* Thông tin duyệt */}
              {article.approvedAt && (
                <div className="content-section">
                  <h3 className="section-title">Thông tin duyệt</h3>
                  <div className="approval-info">
                    <div className="approval-item">
                      <span className="approval-label">Ngày duyệt:</span>
                      <span className="approval-value">{formatDateTime(article.approvedAt)}</span>
                    </div>
                    {article.approver && (
                      <div className="approval-item">
                        <span className="approval-label">Người duyệt:</span>
                        <span className="approval-value">
                          {article.approver.fullName || article.approver.email}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DetailArticle;